import styled from '@emotion/styled/macro'
import { transientProps } from 'lib/styledUtils'
import { Box, Breadcrumbs, FormControl, InputAdornment, MenuItem as MuiMenuItem, Select } from '@mui/material'
import { getCpiCategoryChipColors, resolveCustomChipBg, resolveCustomChipText } from 'lib/utils'
import theme from 'theme'

const ITEM_HEIGHT = 32
const ITEM_PADDING_TOP = 8

export const LibrarySelect = styled(Select)<{ isselected?: string; noborder?: string; iscustom?: string }>`
  min-width: 113px;
  height: 40px;
  background: transparent;
  gap: ${theme.spacing.xSmall}px;
  border-radius: ${theme.attributes.borderRadius.large};
  border: 1px solid ${theme.colors.controlBorder};
  position: relative;

  ${({ noborder }) =>
    noborder &&
    noborder === 'true' &&
    `
    border-radius: 0;
    border: 0;
    border-color: transparent;
    background: transparent;
  `};

  color: ${theme.colors.foreground};
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.large};
  font-weight: ${theme.typography.fontWeight.bold};
  line-height: 20px;
  letter-spacing: 0.1px;
  text-align: left;

  ${({ isselected }) =>
    isselected &&
    isselected === 'true' &&
    `
    background: rgba(234, 221, 255, 0.08);
    
    .MuiOutlinedInput-root {
      .MuiSelect-select {
        color: ${theme.colors.textPrimary} !important;
      }
    }    
  `};

  > * {
    border: none !important;
    outline: none !important;
  }

  .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border: none !important;
  }

  ${({ iscustom }) =>
    iscustom &&
    iscustom === 'true' &&
    `
    min-width: 122px;
    height: 40px;
  `};

  .MuiOutlinedInput-root {
    ${({ iscustom }) =>
      iscustom &&
      iscustom === 'true' &&
      `
      height: 40px;
  `};
  }

  svg.svg-dropdown {
    top: calc(50% - 0.6em);
    right: 13px;

    &.MuiSelect-icon {
      pointer-events: none;
      color: rgba(0, 0, 0, 0.54);
      position: absolute;
    }

    &.MuiSelect-iconClosed {
      transform: rotate(0deg) !important;
    }

    &.MuiSelect-iconOpen {
      transform: rotate(180deg) !important;
    }

    path {
      fill: ${theme.colors.textPrimary} !important;
    }
  }
`

export const Wrapper = styled(Box)<{ isSort?: boolean }>`
  display: flex;
  align-items: center;

  ${({ isSort }) =>
    !isSort &&
    `
   gap: ${theme.spacing.xSmall}px;
  `};

  .label-text {
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.xLarge};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: normal;
    color: ${theme.colors.foreground};
    white-space: nowrap;
  }
`

export const LibraryFormControl = styled(FormControl, transientProps('isSort'))<{
  noborder?: string
  isselected?: string
  iscustom?: string
  isSort?: boolean
  minwidth?: string
}>`
  outline: none;
  min-width: 121px !important;
  height: 40px;
  border-radius: ${theme.attributes.borderRadius.large};
  display: flex;
  position: relative;
  max-width: ${({ isSort }) => (isSort ? 'auto' : '121px')};

  @media (min-width: 1170px) {
    max-width: 200px !important;
  }

  ${({ noborder }) =>
    noborder !== 'true' &&
    `
    border: 1px solid ${theme.colors.controlBorder};
  `};

  ${({ minwidth }) =>
    minwidth &&
    `
      min-width: ${minwidth};
  `};

  .label-text {
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 20px;
    letter-spacing: 0.1px;
    color: ${theme.colors.foreground};
  }

  .MuiOutlinedInput-root {
    background: transparent;
    border: 0;
    color: ${theme.colors.foreground};
    text-align: center;
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 20px;
    letter-spacing: 0.1px;
    gap: 2px;

    .MuiOutlinedInput-input {
      &.Mui-disabled {
        -webkit-text-fill-color: rgba(255, 255, 255, 0.3);
      }

      @media (max-width: 1170px) {
        display: none;
      }
    }
  }

  &:hover {
    border-radius: ${theme.attributes.borderRadius.large};
    background: rgba(234, 221, 255, 0.08);

    .MuiOutlinedInput-root {
      color: ${theme.colors.textPrimary};
    }
  }

  svg {
    top: calc(50% - 0.6em);
    right: ${theme.spacing.small}px;

    path {
      fill: ${theme.colors.textPrimary};
    }
  }

  .MuiSelect-outlined {
    width: auto;
  }

  fieldset {
    padding: 0;
  }
`

export const MenuItem = styled(MuiMenuItem)`
  height: 40px;
  padding-left: ${theme.spacing.small}px;
  padding-right: ${theme.spacing.small}px;

  img {
    margin-right: ${theme.spacing.xSmall}px;
  }

  &:hover {
    background: rgba(208, 188, 255, 0.16);

    html[data-theme='light'] & {
      background: ${theme.colors.menuItemHover};
    }
  }

  &.Mui-selected {
    html[data-theme='light'] & {
      background: ${theme.colors.cardFillTertiary};
    }
  }

  .MuiCheckbox-root {
    padding-right: 19px;
    padding-left: 0;
  }

  .MuiListItemText-root .MuiTypography-root {
    font-family: ${theme.typography.fontFamily.primary};
    font-weight: ${theme.typography.fontWeight.normal};
    font-size: ${theme.typography.fontSize.large};
    line-height: 20px;
    letter-spacing: 0.25px;
    color: ${theme.colors.calendarDay};
  }

  display: flex;
  align-items: center;
  width: 100%;

  .image-wrapper {
    display: flex;

    > img {
      width: 18px;
      height: 18px;
      box-shadow: ${theme.attributes.boxShadow.chipCircle};
      border-radius: ${theme.attributes.borderRadius.circle};

      &:nth-of-type(1) {
        z-index: 10;
      }

      &:nth-of-type(2) {
        z-index: 9;
      }

      &:nth-of-type(3) {
        z-index: 8;
      }

      &:nth-of-type(4) {
        z-index: 7;
      }

      &:nth-of-type(5) {
        z-index: 6;
      }

      &:nth-of-type(6) {
        z-index: 5;
      }

      &:nth-of-type(7) {
        z-index: 4;
      }
    }
  }
`

export const CategoryMenuProps = {
  PaperProps: {
    sx: {
      background: theme.colors.backgroundDark,
      boxShadow: theme.attributes.boxShadow.selectFieldPaper,
      borderRadius: theme.attributes.borderRadius.small,
      minWidth: '321px !important',
      maxHeight: '600px !important',

      '.MuiList-root': {
        paddingTop: '0px',
        paddingBottom: '0px',
        maxHeight: '248px'
      },

      '.MuiMenuItem-root': {
        height: '48px',
        paddingLeft: `${theme.spacing.small}px`,
        paddingRight: `${theme.spacing.small}px`,

        img: {
          marginRight: `${theme.spacing.xSmall}px`
        },

        '&:hover': {
          background: 'rgba(208, 188, 255, 0.16) !important',
          "html[data-theme='light'] &": {
            background: `${theme.colors.menuItemHover} !important`
          }
        },

        '&.Mui-selected, &.Mui-focusVisible': {
          "html[data-theme='light'] &": {
            background: `${theme.colors.cardFillTertiary} !important`
          }
        },

        '.MuiCheckbox-root': {
          padding: `${theme.spacing.xSmall}px`,

          '&:hover': {
            background: 'rgba(202, 196, 208, 0.12)'
          }
        },

        '.MuiListItemText-root .MuiTypography-root': {
          fontFamily: theme.typography.fontFamily.primary,
          fontWeight: theme.typography.fontWeight.bold,
          fontSize: theme.typography.fontSize.xLarge,
          lineHeight: '24px',
          letterSpacing: '0.1px',
          color: theme.colors.neutral,
          paddingLeft: '19px'
        },
        display: 'flex',
        alignItems: 'center',
        width: '100%',

        '.sources-count-text': {
          color: theme.colors.legacyWhite4,
          fontSize: theme.typography.fontSize.xLarge,
          fontStyle: 'normal',
          fontWeight: theme.typography.fontWeight.normal,
          lineHeight: 'normal',
          marginLeft: `${theme.spacing.xSmall}px`
        },

        '.image-wrapper': {
          display: 'flex',

          '> img': {
            width: '18px',
            height: '18px',
            boxShadow: theme.attributes.boxShadow.chipCircle,
            margin: '-4.5px',
            borderRadius: theme.attributes.borderRadius.circle,

            '&:nth-of-type(1)': {
              zIndex: 10
            },
            '&:nth-of-type(2)': {
              zIndex: 9
            },
            '&:nth-of-type(3)': {
              zIndex: 8
            },
            '&:nth-of-type(4)': {
              zIndex: 7
            },
            '&:nth-of-type(5)': {
              zIndex: 6
            },
            '&:nth-of-type(6)': {
              zIndex: 5
            },
            '&:nth-of-type(7)': {
              zIndex: 4
            }
          }
        },

        '.textWrapper': {
          marginLeft: '13px'
        }
      },

      '&::-webkit-scrollbar-track': {
        background: theme.baseColors.surface[50],
        "html[data-theme='light'] &": {
          background: 'transparent'
        }
      },

      '&::-webkit-scrollbar-thumb': {
        background: theme.colors.textPrimary,
        height: '4px',
        width: '4px',
        border: '4px solid transparent',
        backgroundClip: 'content-box',
        "html[data-theme='light'] &": {
          background: theme.colors.dropdownBorder
        }
      }
    },
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP
    }
  },
  anchorOrigin: {
    vertical: 'bottom' as const,
    horizontal: 'left' as const
  },
  transformOrigin: {
    vertical: 'top' as const,
    horizontal: 'left' as const
  },
  variant: 'menu' as const
}

export const DataSourceInputAdornment = styled(InputAdornment)`
  .adornment-wrapper {
    display: flex;
    gap: ${theme.spacing.xSmall}px;

    .sources-count-text {
      color: ${theme.colors.legacyNeutral2};
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.medium};
      font-style: normal;
      font-weight: ${theme.typography.fontWeight.normal};
      line-height: 16px;
      letter-spacing: 0.4px;
    }

    img {
      width: 18px;
      height: 18px;
      border-radius: ${theme.attributes.borderRadius.circle};

      &:nth-of-type(1) {
        z-index: 10;
      }

      &:nth-of-type(2) {
        z-index: 9;
      }

      &:nth-of-type(3) {
        z-index: 8;
      }

      &:nth-of-type(4) {
        z-index: 7;
      }

      &:nth-of-type(5) {
        z-index: 6;
      }

      &:nth-of-type(6) {
        z-index: 5;
      }

      &:nth-of-type(7) {
        z-index: 4;
      }
    }
  }
`

export const IntegrationItem = styled(Box)<{ active?: boolean; iserrored?: string }>`
  position: relative;

  img {
    border: 1px solid ${theme.colors.controlBorder};
    border-radius: ${theme.attributes.borderRadius.circle};

    ${({ active }) =>
      active &&
      `
       border: 1px solid ${theme.baseColors.info[20]};
    `};
  }

  span {
    width: 8px;
    height: 8px;
    border-radius: ${theme.attributes.borderRadius.circle};
    display: block;
    background: ${theme.colors.controlBorder};
    position: absolute;
    right: -3px;
    top: ${theme.spacing.sm}px;

    ${({ active }) =>
      active &&
      `
        background: ${theme.baseColors.info[20]};
    `};
  }

  .circle-new {
    z-index: 10;
    top: ${theme.spacing.xsm}px;
    right: -7px;
  }

  ${({ iserrored }) =>
    iserrored &&
    iserrored === 'true' &&
    `
      span {
        background: ${theme.baseColors.info[50]};
      }
      
      img {
        border-color: ${theme.baseColors.info[50]};
      }
  `};
`

export const LibraryCardWrapper = styled(Box)`
  background: ${theme.colors.backgroundLight};
  border-radius: ${theme.attributes.borderRadius.quaternary};
  padding: ${theme.spacing.small}px;
  margin-bottom: ${theme.spacing.medium}px;
  width: 880px;
  position: relative;
  cursor: pointer;

  &:hover {
    background: ${theme.colors.legacy342f32};
  }

  .cpi-new {
    border-radius: 18px;
    background: ${theme.baseColors.info[10]};
    box-shadow: ${theme.attributes.boxShadow.quaternary};

    html[data-theme='light'] & {
      box-shadow: none;
    }
    display: flex;
    padding: 2px ${theme.spacing.xSmall}px;
    align-items: center;
    gap: ${theme.spacing.xSmall}px;
    margin-left: ${theme.spacing.xSmall}px;

    > .MuiTypography-root {
      color: ${theme.colors.white};
      text-align: center;
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.medium};
      font-weight: ${theme.typography.fontWeight.bold};
      line-height: 16px;
      letter-spacing: 0.5px;
    }
  }

  .favorite-icon {
    position: absolute;
    top: 5px;
    right: ${theme.spacing.small}px;

    .MuiButtonBase-root {
      &:hover,
      &:focus,
      &:active {
        background: rgba(202, 196, 208, 0.08);
      }
    }

    /* The outline star strokes currentColor; #debdd9 is the original dark-theme star colour. */
    svg path[fill='none'] {
      stroke: #debdd9;
    }

    /* Unfilled star carries fill="none", filled star fill="currentColor" — scope by that. */
    html[data-theme='light'] & {
      svg path[fill='none'] {
        stroke: #5b4c56;
      }

      svg path[fill='currentColor'] {
        fill: ${theme.colors.cta};
        stroke: ${theme.colors.cta};
      }
    }
  }

  @media (min-width: 1280px) {
    width: 920px;
  }

  .title-wrapper {
    display: flex;
    align-items: center;
    margin: ${theme.spacing.lg}px 0 ${theme.spacing.xSmall}px;

    .title {
      color: ${theme.colors.foreground};
      text-align: start;
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.xxLarge};
      font-weight: ${theme.typography.fontWeight.normal};
      line-height: 28px;
    }
  }

  .cpi-name {
    display: inline-block;
    padding: 2px ${theme.spacing.xSmall}px;
    align-items: center;
    gap: 3px;
    border-radius: 18px;
    background: ${theme.colors.cpiChipBg};
    box-shadow: ${theme.attributes.boxShadow.quaternary};

    html[data-theme='light'] & {
      box-shadow: none;
    }

    > .MuiTypography-root {
      color: ${theme.colors.cpiChipText};
      text-align: center;
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.medium};
      font-weight: ${theme.typography.fontWeight.bold};
      line-height: 16px;
      letter-spacing: 0.5px;
    }
  }

  .text {
    color: ${theme.colors.foreground};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.xLarge};
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 24px;
    letter-spacing: 0.5px;
  }
`

export const LibraryFooter = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${theme.spacing.medium}px;

  .left-footer {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    gap: ${theme.spacing.small}px;

    .data-sources {
      display: flex;
      align-items: center;
      border-radius: ${theme.attributes.borderRadius.secondary};
      border: 1px solid ${theme.colors.borderLow};
      padding: 7px ${theme.spacing.xSmall}px;
      position: relative;

      html[data-theme='light'] & {
        border-color: #dcd2d9;
      }

      > .MuiTypography-root {
        color: ${theme.colors.legacyNeutral2};
        font-family: ${theme.typography.fontFamily.primary};
        font-size: ${theme.typography.fontSize.medium};
        font-weight: ${theme.typography.fontWeight.normal};
        line-height: 16px;
        letter-spacing: 0.4px;
      }

      .data-sources-wrapper {
        display: flex;
        align-items: center;
        margin-left: 7px;
        gap: ${theme.spacing.half}px;

        .error-wrapper {
          svg {
            &:first-of-type {
              left: ${theme.spacing.sm}px !important;
            }
            &:last-of-type {
              left: ${theme.spacing.md}px !important;
            }
          }
        }

        .data-sources-count-text {
          color: ${theme.colors.legacyNeutral2};
          font-family: ${theme.typography.fontFamily.primary};
          font-size: ${theme.typography.fontSize.medium};
          font-style: normal;
          font-weight: ${theme.typography.fontWeight.normal};
          line-height: 16px;
          letter-spacing: 0.4px;
          margin-left: ${theme.spacing.xSmall}px;
        }
      }
    }

    .framework-panel {
      border-radius: ${theme.attributes.borderRadius.secondary};
      border: 1px solid ${theme.colors.borderLow};
      padding: 7px ${theme.spacing.xSmall}px;

      html[data-theme='light'] & {
        border-color: #dcd2d9;
      }

      img {
        width: 24px;
        height: 24px;
      }
    }
  }

  .actions-wrapper {
    display: flex;
    align-items: center;
    height: 100%;

    .MuiButtonBase-root {
      margin-left: ${theme.spacing.small}px;
    }
  }
`

export const LibraryFooterCategory = styled(Box)<{ category: string }>`
  display: flex;
  height: 30px;
  align-items: center;
  gap: ${theme.spacing.xSmall}px;
  border-radius: ${theme.attributes.borderRadius.secondary};
  box-shadow: ${theme.attributes.boxShadow.quaternary};
  padding: ${theme.spacing.xsm}px 16px;

  html[data-theme='light'] & {
    box-shadow: none;
  }

  > .MuiTypography-root {
    text-align: center;
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.medium};
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 16px;
    letter-spacing: 0.5px;
  }

  ${({ category }) => {
    const colors = getCpiCategoryChipColors(category)

    return `
      background: ${colors.background};

      > .MuiTypography-root {
        color: ${colors.color};
    `
  }};
`

export const LibraryFooterCustomCategory = styled(Box)<{ bgColor: string; textColor: string }>`
  display: flex;
  height: 30px;
  align-items: center;
  gap: ${theme.spacing.xSmall}px;
  border-radius: ${theme.attributes.borderRadius.secondary};
  box-shadow: ${theme.attributes.boxShadow.quaternary};
  padding: ${theme.spacing.xsm}px 16px;
  background: ${({ bgColor }) => resolveCustomChipBg(bgColor)};

  html[data-theme='light'] & {
    box-shadow: none;
  }

  > .MuiTypography-root {
    text-align: center;
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.medium};
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 16px;
    letter-spacing: 0.5px;
    color: ${({ textColor }) => resolveCustomChipText(textColor)};
  }
`

export const LibraryFooterLogo = styled(Box)<{ logo: string }>`
  border: 1px solid #342f32;
  box-shadow: ${theme.attributes.boxShadow.overallScoreWrapper};
  width: 24px;
  height: 24px;
  border-radius: ${theme.attributes.borderRadius.normal};
  background: ${({ logo }) => `url('${logo}') no-repeat center`};
  background-size: cover;
  margin-left: ${theme.spacing.half}px;
`

export const HeaderBreadcrumbs = styled(Breadcrumbs)`
  .MuiLink-root,
  .current-breadcrumb {
    font-family: ${theme.typography.fontFamily.primary};
    font-weight: ${theme.typography.fontWeight.bold};
    font-size: ${theme.typography.fontSize.medium};
    line-height: 16px;
    letter-spacing: 0.5px;
    text-decoration: none;

    &.category-breadcrumb {
      color: ${theme.colors.white};
      text-align: center;
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.mediumSmall};
      font-style: normal;
      font-weight: ${theme.typography.fontWeight.bold};
      line-height: 16px;
      letter-spacing: 0.5px;
      display: flex;
      padding: 2px ${theme.spacing.xSmall}px;
      align-items: center;
      gap: ${theme.spacing.xSmall}px;
      border-radius: ${theme.attributes.borderRadius.secondary};
      background: #b15c0f;
      box-shadow: ${theme.attributes.boxShadow.quaternary};
      text-transform: capitalize;
    }
  }

  .MuiLink-root {
    color: #debdd9;
  }

  .current-breadcrumb {
    color: ${theme.colors.textPrimary};
  }

  .MuiBreadcrumbs-separator {
    color: #debdd9;
    margin-left: ${theme.spacing.xSmall}px;
    margin-right: ${theme.spacing.xSmall}px;
  }
`
